/**
 * Thrown when a user attempts to access a premium feature without a valid license.
 *
 * Corresponds to HTTP 403 from the backend with `code: "premium_required"`.
 * Check `error.feature` to know which feature gate triggered the error.
 *
 * @example
 * ```ts
 * try {
 *   await sdk.finops.dashboard();
 * } catch (e) {
 *   if (e instanceof PremiumFeatureError) {
 *     showUpgradeModal(e.feature); // e.g. "agentic_orchestration"
 *   }
 * }
 * ```
 */
export class PremiumFeatureError extends Error {
    feature;
    constructor(
    /** The feature key that requires a premium license (e.g. "agentic_orchestration") */
    feature, message = `Premium license required for feature: ${feature}`) {
        super(message);
        this.feature = feature;
        this.name = 'PremiumFeatureError';
        Object.setPrototypeOf(this, PremiumFeatureError.prototype);
    }
}
/**
 * Thrown when a user attempts to access a feature without sufficient permissions.
 *
 * @deprecated Prefer {@link PremiumFeatureError} for premium gate errors.
 * `AccessDeniedError` is kept for backward compatibility.
 */
export class AccessDeniedError extends Error {
    feature;
    constructor(feature, message = 'Access denied for feature: ' + feature) {
        super(message);
        this.feature = feature;
        this.name = 'AccessDeniedError';
        // Set the prototype explicitly to restore prototype chain in compiled JS.
        Object.setPrototypeOf(this, AccessDeniedError.prototype);
    }
}
/**
 * Thrown when the backend input guardrail blocks a message (HTTP 403 with
 * body `{ detail, blocked: true }` — Sprint 6.8-01 hard-403 pre-stream).
 *
 * Distinct from {@link AccessDeniedError} (auth/licence): a blocked message
 * must NOT be retried as-is — the content itself was rejected.
 */
export class SecurityBlockedError extends Error {
    reason;
    status = 403;
    constructor(reason, message = `Message blocked by security policy: ${reason}`) {
        super(message);
        this.reason = reason;
        this.name = 'SecurityBlockedError';
        Object.setPrototypeOf(this, SecurityBlockedError.prototype);
    }
}
/**
 * Thrown when the backend is unavailable (circuit breaker Open) **in production**.
 *
 * En production (`demoMode: false`), le SDK ne fabrique JAMAIS de données : il
 * remonte cette erreur pour que l'UI affiche un état « service indisponible »
 * plutôt qu'un contenu de démonstration. En mode démo (`demoMode: true`), le
 * fallback de simulation est utilisé à la place (pas d'erreur).
 */
export class BackendUnavailableError extends Error {
    constructor(message = 'Backend indisponible (circuit ouvert).') {
        super(message);
        this.name = 'BackendUnavailableError';
        Object.setPrototypeOf(this, BackendUnavailableError.prototype);
    }
}
//# sourceMappingURL=errors.js.map